import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { usePlatformAuth } from "../../context/PlatformAuthContext";
import { ImpersonationModal } from "./ImpersonationModal";
import { HomyLogo } from "../brand/HomyLogo";
import {
  LayoutDashboard,
  Building2,
  ScrollText,
  UserCheck,
  LogOut,
  Terminal,
  Megaphone,
  Sun,
  Moon,
  ShieldCheck,
  Headphones,
  Workflow,
  Store,
  Home,
} from "lucide-react";
import { Badge, Button } from "@homy/ui";
import { getTheme, setTheme } from "../../lib/theme";

export function PlatformLayout() {
  const { operator, logout } = usePlatformAuth();
  const navigate = useNavigate();
  const [impersonationOpen, setImpersonationOpen] = useState(false);
  const [theme, setLocalTheme] = useState(() => getTheme());

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    setLocalTheme(next);
  };

  const handleSignOut = () => {
    logout();
    navigate("/login");
  };

  const navItems = [
    {
      to: "/overview",
      label: "Overview",
      icon: LayoutDashboard,
      description: "Platform KPIs & Health",
    },
    {
      to: "/organizations",
      label: "Organizations",
      icon: Building2,
      description: "Tenant Directory & Status",
    },
    {
      to: "/publications",
      label: "Publications",
      icon: Megaphone,
      description: "Active by Country & Zone",
    },
    {
      to: "/marketplaces/vendors",
      label: "Vendors Marketplace",
      icon: Store,
      description: "Directory, Categories & Claims",
    },
    {
      to: "/marketplaces/properties",
      label: "Properties Marketplace",
      icon: Home,
      description: "Listing Directory & Syndication",
    },
    {
      to: "/temporal",
      label: "Temporal Jobs",
      icon: Workflow,
      description: "Scheduled Workflows & Cadences",
    },
    {
      to: "/support",
      label: "Platform Support",
      icon: Headphones,
      description: "Sovereign Inquiries & SLA Queue",
    },
    {
      to: "/audit-logs",
      label: "Audit Logs",
      icon: ScrollText,
      description: "Cross-Tenant Audit Trail",
    },
    {
      to: "/settings",
      label: "Security Settings",
      icon: ShieldCheck,
      description: "Admin Credentials & MFA",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* Distinctive Top Sovereign Accent Ribbon */}
      <div className="h-1 w-full bg-gradient-to-r from-amber-500 via-rose-500 to-indigo-600 shrink-0 z-50" />

      {/* Top Console Header — distinctive command center styling */}
      <header className="h-16 border-b border-purple-900/30 bg-[#0f0e17] text-slate-100 shadow-sm sticky top-0 z-40 px-6 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-4">
          <HomyLogo variant="header" subtitle="Sovereign Control Plane" />

          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-mono font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            SOVEREIGN PLATFORM CONTROL
          </span>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-xs h-8 border-slate-700 bg-slate-800/80 text-slate-200 hover:bg-slate-700 hover:text-white"
            onClick={() => setImpersonationOpen(true)}
          >
            <UserCheck className="h-3.5 w-3.5 text-amber-400" />
            Launch Impersonation
          </Button>

          {/* Theme Switcher Button */}
          <button
            type="button"
            onClick={toggleTheme}
            aria-label="Toggle light/dark theme"
            className="p-2 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-colors cursor-pointer"
            title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
          >
            {theme === "dark" ? (
              <Sun className="h-4 w-4 text-amber-400" />
            ) : (
              <Moon className="h-4 w-4 text-slate-300" />
            )}
          </button>

          {/* Operator Identity Pill */}
          <div className="flex items-center gap-2 pl-3 border-l border-slate-800">
            <div className="flex flex-col text-right">
              <span className="text-xs font-medium text-slate-200">
                {operator?.email || "platform@homy.com"}
              </span>
              <span className="text-[10px] font-mono text-amber-400 font-semibold">
                {operator?.role || "PLATFORM_ADMIN"}
              </span>
            </div>

            <Button
              variant="ghost"
              size="icon"
              title="Sign out"
              className="h-8 w-8 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10"
              onClick={handleSignOut}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Body: Sidebar + Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <aside className="w-64 border-r border-border bg-card/40 p-4 flex flex-col justify-between shrink-0">
          <div className="space-y-6">
            <div>
              <p className="px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                Administration Hubs
              </p>
              <nav className="space-y-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `flex items-start gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                          isActive
                            ? "bg-primary/15 text-primary font-medium border border-primary/20"
                            : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                        }`
                      }
                    >
                      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
                      <div>
                        <div className="font-medium leading-none mb-1">{item.label}</div>
                        <div className="text-[11px] text-muted-foreground/80 leading-tight">
                          {item.description}
                        </div>
                      </div>
                    </NavLink>
                  );
                })}
              </nav>
            </div>

            <div className="rounded-lg border border-border/80 bg-muted/20 p-3 text-xs space-y-2">
              <div className="flex items-center gap-1.5 text-muted-foreground font-medium">
                <Terminal className="h-3.5 w-3.5 text-primary" />
                <span>Security Guards</span>
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Platform endpoints are physically protected by <code>require_platform_admin</code>. Read-only impersonation strictly blocks mutations.
              </p>
            </div>
          </div>

          <div className="pt-4 border-t border-border/50 text-[11px] text-muted-foreground flex justify-between items-center px-2 font-mono">
            <span>RFC 6265bis</span>
            <span className="text-emerald-400">Strict Cookie</span>
          </div>
        </aside>

        {/* Content Outlet */}
        <main className="flex-1 overflow-y-auto bg-background p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      <ImpersonationModal
        open={impersonationOpen}
        onOpenChange={setImpersonationOpen}
      />
    </div>
  );
}
